import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, NgZone } from '@angular/core';

@Directive({
  selector: '[zebraShadowRows]',
  standalone: true,
})
export class ZebraShadowRowsDirective implements AfterViewInit, OnDestroy {
  @Input() zebraRowSelector = '.row';
  @Input() zebraOddColor = '#F7F8FB';
  @Input() zebraEvenColor = '#ffffff';

  @Input() zebraRetryCount = 20;
  @Input() zebraRetryDelayMs = 50;

  private observer?: MutationObserver;
  private destroyed = false;

  // Debounce so we apply AFTER component finishes its repaint cycle
  private rafId: number | null = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => this.initWithRetries());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.observer?.disconnect();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
  }

  private initWithRetries() {
    let attemptsLeft = this.zebraRetryCount;

    const tryInit = () => {
      if (this.destroyed) return;

      const shadow = this.host.nativeElement.shadowRoot;
      if (!shadow) {
        if (attemptsLeft-- > 0) setTimeout(tryInit, this.zebraRetryDelayMs);
        return;
      }

      // Apply once after initial render
      this.scheduleApply(shadow);

      this.observer = new MutationObserver(() => this.scheduleApply(shadow));
      this.observer.observe(shadow, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    };

    setTimeout(tryInit, 0);
  }

  private scheduleApply(shadow: ShadowRoot) {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.applyZebra(shadow);
    });
  }

  private applyZebra(shadow: ShadowRoot) {
    const rows = shadow.querySelectorAll<HTMLElement>(this.zebraRowSelector);
    if (!rows.length) return;

    rows.forEach((row, i) => {
      // Use backgroundColor so we don't clobber other inline background styles
      row.style.backgroundColor = i === 0 ? '#ffffff' : i % 2 === 0 ? this.zebraOddColor : this.zebraEvenColor;
    });
  }
}
